import { useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { fetchData } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import {
  Card,
  Empty,
  ErrorBanner,
  FormattedDate,
  Loading,
  PageHeader,
  Tag,
} from '../../components/ui'
import {
  MaterialRequestLifecyclePanel,
  type LifecycleBatch,
} from './MaterialRequestLifecyclePanel'

type Singleton = {
  id: string
  request_number?: string
  status: string
  project_id: string
  created_at: number
  total_quantity?: number
  notes?: string | null
}

type BatchGroup = LifecycleBatch & {
  created_at: number
  item_count: number
}

type Row =
  | { kind: 'single'; key: string; title: string; subtitle?: string; status: string; created_at: number }
  | {
      kind: 'batch'
      key: string
      title: string
      subtitle?: string
      status: string
      created_at: number
      batch: BatchGroup
    }

// The editor posts to /material-requests/batch (batch_id IS NOT NULL) which the
// regular list endpoint excludes by design. Fetch both endpoints and merge so
// every submission shows up regardless of how it was created.
export function ProjectMaterialRequestsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const [expanded, setExpanded] = useState<string | null>(null)
  const [singles, batches] = useQueries({
    queries: [
      {
        queryKey: ['material-requests', projectId],
        enabled: Boolean(projectId),
        queryFn: () =>
          fetchData<Singleton[]>(orgPath(`/material-requests?project_id=${projectId}`)),
      },
      {
        queryKey: ['material-requests', 'bulk', projectId],
        enabled: Boolean(projectId),
        queryFn: () =>
          fetchData<BatchGroup[]>(orgPath(`/material-requests/bulk?project_id=${projectId}`)),
      },
    ],
  })

  if (singles.isLoading || batches.isLoading) return <Loading />
  if (singles.isError) return <ErrorBanner error={singles.error} />
  if (batches.isError) return <ErrorBanner error={batches.error} />

  const rows: Row[] = [
    ...(singles.data ?? []).map<Row>((r) => ({
      kind: 'single',
      key: `s:${r.id}`,
      title: r.request_number ?? r.id.slice(0, 8),
      subtitle: r.notes ?? undefined,
      status: r.status,
      created_at: r.created_at,
    })),
    ...(batches.data ?? []).map<Row>((b) => ({
      kind: 'batch',
      key: `b:${b.batch_id}`,
      title: `Batch ${b.batch_id.slice(0, 8)} · ${b.item_count} item${b.item_count === 1 ? '' : 's'}`,
      subtitle: b.category ?? undefined,
      status: b.status,
      created_at: b.created_at,
      batch: b,
    })),
  ].sort((a, b) => b.created_at - a.created_at)

  return (
    <div>
      <PageHeader
        title="Material requests"
        actions={
          <Link
            to="new"
            className="rounded-md bg-accent-500 hover:bg-accent-600 px-3 py-1.5 text-sm font-medium text-white"
          >
            New request
          </Link>
        }
      />
      {rows.length === 0 ? (
        <Empty
          message="No material requests yet."
          hint="Click “New request” to file the first one for this project."
        />
      ) : (
        <Card>
          <ul className="divide-y divide-surface-700 text-sm">
            {rows.map((r) => {
              const isOpen = r.kind === 'batch' && expanded === r.key
              return (
                <li key={r.key} className="py-3">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex items-start gap-2">
                      {r.kind === 'batch' && (
                        <button
                          type="button"
                          aria-label={isOpen ? 'Collapse' : 'Expand'}
                          className="text-surface-400 hover:text-accent-400 mt-0.5 w-5 text-left"
                          onClick={() =>
                            setExpanded((cur) => (cur === r.key ? null : r.key))
                          }
                        >
                          {isOpen ? '▾' : '▸'}
                        </button>
                      )}
                      <div>
                        <div className="font-medium">{r.title}</div>
                        {r.subtitle && (
                          <div className="text-surface-400 text-xs mt-1">{r.subtitle}</div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <Tag tone={statusTone(r.status)}>{r.status}</Tag>
                      <div className="text-xs text-surface-400 mt-1">
                        <FormattedDate ts={r.created_at} />
                      </div>
                    </div>
                  </div>
                  {isOpen && r.kind === 'batch' && (
                    <MaterialRequestLifecyclePanel batch={r.batch} />
                  )}
                </li>
              )
            })}
          </ul>
        </Card>
      )}
    </div>
  )
}

function statusTone(status: string): 'default' | 'success' | 'warn' | 'danger' {
  const s = status.toLowerCase()
  if (s.includes('approved') || s.includes('completed') || s.includes('received') || s === 'paid')
    return 'success'
  if (s.includes('pending') || s.includes('draft') || s.includes('partial') || s === 'requested' || s === 'billed')
    return 'warn'
  if (s.includes('rejected') || s.includes('cancelled')) return 'danger'
  return 'default'
}
